import React from 'react';
import { isMonday } from 'date-fns';
import { Box, Checkbox, FormControl, FormControlLabel, FormGroup } from '@mui/material';
import { Uniform } from '../../../../components/uniform/uniform';
import { UniformStepContainer } from '../../../../components/uniform/uniform-step-container';
import { UniformStep } from '../../../../components/uniform/uniform-step';
import { PrintScheduleTitlezone } from './print-schedule-titlezone';
import { PrintScheduleLaunchbox } from './print-schedule-launchbox';
import { Accordion, AccordionSummary, AccordionDetails } from '../../../../components/accordion';
import { Row } from '../../../../components/row';
import { Cell } from '../../../../components/cell';
import { PrintScheduleState, PrintScheduleStateErrors } from './interfaces';
import { DatePicker, DatePickerValue } from '../../../../components/date-picker';
import { ScheduleDisplayMode } from '../../../../api-service/clients';
import { SingleSelect } from '../../../../components/single-select';

interface IPrintScheduleComponentProps {
  title: string;
  completeLabel: string;
  isEditmode: boolean;
  isLoading: boolean;
  onPrintScheduleClick: () => void;
  onValidate: () => boolean;
  printSetup: PrintScheduleState;
  displayModes: ScheduleDisplayMode[];
  onPrintSetupChange: <K extends keyof PrintScheduleState>(name: K, value: PrintScheduleState[K]) => void;
  errors: PrintScheduleStateErrors;
}

export function PrintScheduleComponent(props: IPrintScheduleComponentProps) {
  const { title, completeLabel, printSetup, displayModes, errors, isEditmode, onPrintScheduleClick, isLoading, onValidate, onPrintSetupChange } = props;

  const { startDate, endDate, displayMode, includeAbsences, includeInterviews } = printSetup;
  const isWeekScheme = displayMode === ScheduleDisplayMode.WeekScheme;

  return (
    <Uniform
      titleZone={<PrintScheduleTitlezone title={title} />}
      launchbox={<PrintScheduleLaunchbox isLoading={isLoading} onSave={onPrintScheduleClick} onValidate={onValidate} />}
      isEditMode={isEditmode}
      showButtons={!isEditmode}
      onOk={onPrintScheduleClick}
      labels={{ completeLabel }}
      onCancel={() => window.close()}
      okButtonIsLoading={isLoading}
      showSpinnerOnNextAndComplete={isLoading}
    >
      <UniformStep stepName="Generelt" validate={onValidate}>
        <UniformStepContainer>
          <Accordion defaultExpanded>
            <AccordionSummary>Generelt</AccordionSummary>
            <AccordionDetails>
              <Box mt={1}>
                <Row>
                  <Cell size="thirds">
                    <SingleSelect<ScheduleDisplayMode>
                      label="Visning"
                      getOptionLabel={option => getDisplayMode(option) || ''}
                      onChange={(value: ScheduleDisplayMode) => onPrintSetupChange('displayMode', value)}
                      options={displayModes}
                      value={displayMode}
                      variant="outlined"
                      renderOption={(prop: object, option: ScheduleDisplayMode) => (
                        <li {...prop} key={option}>
                          {getDisplayMode(option)}
                        </li>
                      )}
                      required
                      error={!!errors.displayMode}
                      errorText={errors.displayMode}
                    />
                  </Cell>
                  <Cell size="thirds">
                    <DatePicker
                      required
                      label="Startdato"
                      shouldDisableDate={isWeekScheme ? date => !isMonday(date) : undefined}
                      fullWidth
                      variant="outlined"
                      onChange={(date: DatePickerValue) => onPrintSetupChange('startDate', date)}
                      value={startDate}
                      error={!!errors.startDate}
                      errorText={errors.startDate}
                    />
                  </Cell>
                  <Cell size="thirds">
                    <DatePicker
                      required
                      label="Slutdato"
                      disabled={isWeekScheme}
                      fullWidth
                      variant="outlined"
                      onChange={(date: DatePickerValue) => onPrintSetupChange('endDate', date)}
                      value={endDate}
                      error={!!errors.endDate}
                      errorText={errors.endDate}
                    />
                  </Cell>
                </Row>
                <Row>
                  <Cell size="full">
                    <FormControl component="fieldset" variant="standard">
                      <FormGroup>
                        <Box>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="includeAbsences"
                                onChange={(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => onPrintSetupChange('includeAbsences', checked)}
                                checked={includeAbsences}
                                disabled={isWeekScheme}
                              />
                            }
                            label="Medtag fravær og fritagelse"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="includeInterviews"
                                onChange={(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => onPrintSetupChange('includeInterviews', checked)}
                                checked={includeInterviews}
                                disabled={isWeekScheme}
                              />
                            }
                            label="Medtag samtaler"
                          />
                        </Box>
                      </FormGroup>
                    </FormControl>
                  </Cell>
                </Row>
              </Box>
            </AccordionDetails>
          </Accordion>
        </UniformStepContainer>
      </UniformStep>
    </Uniform>
  );
}

function getDisplayMode(displayMode: ScheduleDisplayMode) {
  switch (displayMode) {
    case ScheduleDisplayMode.Day:
      return 'Dag';
    case ScheduleDisplayMode.Week:
      return 'Uge';
    case ScheduleDisplayMode.Month:
      return 'Måned';
    case ScheduleDisplayMode.List:
      return 'Liste';
    case ScheduleDisplayMode.WeekScheme:
      return 'Ugeskema (mandag-fredag)';
    default:
      return 'Ukendt';
  }
}
